import { Component, signal, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { EventosService, EventoEnVivo } from '../services/eventos.service';
import { NotificationService } from '../services/notification.service';

interface LiveEvent {
  id: number;
  title: string;
  instructor: string;
  date: Date;
  time: string;
  duration: string;
  attendees: number;
  maxAttendees: number;
  category: string;
  thumbnail: string;
  color: string;
}

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  events: LiveEvent[];
}

@Component({
  selector: 'app-calendar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './calendar.html',
  styleUrl: './calendar.css',
})
export class CalendarComponent implements OnInit {
  private eventosService = inject(EventosService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);

  currentDate = signal(new Date());
  selectedDate = signal<Date | null>(null);
  calendarDays = signal<CalendarDay[]>([]);
  viewMode = signal<'month' | 'week'>('month');
  isLoading = signal(false);
  eventosReales = signal<EventoEnVivo[]>([]);

  weekDays = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  // Eventos mapeados para el calendario
  liveEvents: LiveEvent[] = [];

  ngOnInit() {
    window.scrollTo(0, 0);
    this.loadEventos();
  }

  /**
   * Carga eventos del backend
   */
  loadEventos() {
    this.isLoading.set(true);
    
    this.eventosService.getMisEventos().subscribe({
      next: (response) => {
        if (response.ok && response.data) {
          console.log('📅 Eventos cargados para el calendario:', response.data.length);
          this.eventosReales.set(response.data);
          this.mapEventosToCalendar(response.data);
          this.generateCalendar();
        }
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error al cargar eventos:', error);
        this.notificationService.error('Error', 'No se pudieron cargar los eventos');
        this.isLoading.set(false);
        this.generateCalendar();
      }
    });
  }

  /**
   * Convierte una fecha UTC string a Date tratándola como hora local
   * Las fechas en la BD vienen con 'Z' pero ya están en hora local de El Salvador
   */
  private parseAsLocalDate(dateString: string): Date {
    // Remover la 'Z' y cualquier indicador de zona horaria
    const cleanDateString = dateString.replace('Z', '').replace(/[+-]\d{2}:\d{2}$/, '');
    
    // Parsear como fecha local
    const date = new Date(cleanDateString);
    
    return date;
  }

  /**
   * Mapea los eventos del backend al formato del calendario
   */
  private mapEventosToCalendar(eventos: EventoEnVivo[]) {
    this.liveEvents = eventos.map(evento => {
      const fechaInicio = this.parseAsLocalDate(evento.fecha_inicio);
      
      // Construir URL de la imagen desde Google Cloud Storage
      const imagenPortada = evento.imagen_portada 
        ? `https://storage.googleapis.com/traidinglab/eventos/portadas/${evento.imagen_portada}`
        : 'https://storage.googleapis.com/traidinglab/eventos/portadas/e823bbf3-5eaf-492e-8613-fb6574a81c33.jpeg'; // Imagen por defecto
      
      return {
        id: evento.id,
        title: evento.titulo,
        instructor: evento.instructor,
        date: fechaInicio,
        time: fechaInicio.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
        duration: this.eventosService.getDuracionEvento(evento),
        attendees: evento.inscritos,
        maxAttendees: evento.capacidad_maxima,
        category: evento.estado === 'EN_VIVO' ? 'EN VIVO' : evento.estado,
        thumbnail: imagenPortada,
        color: evento.color || '#0e40d8'
      };
    });
    
    console.log('🖼️ Imágenes del calendario:', this.liveEvents.map(e => ({ titulo: e.title, imagen: e.thumbnail })));
  }

  generateCalendar() {
    const year = this.currentDate().getFullYear();
    const month = this.currentDate().getMonth();
    
    // Primer día del mes
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Día de la semana del primer día (0 = Domingo)
    const startingDayOfWeek = firstDay.getDay();
    
    const days: CalendarDay[] = [];
    
    // Días del mes anterior
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonthLastDay - i);
      days.push({
        date,
        isCurrentMonth: false,
        isToday: this.isToday(date),
        events: this.getEventsForDate(date)
      });
    }
    
    // Días del mes actual
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day);
      days.push({
        date,
        isCurrentMonth: true,
        isToday: this.isToday(date),
        events: this.getEventsForDate(date)
      });
    }
    
    // Días del mes siguiente para completar la grilla
    const remainingDays = 42 - days.length; // 6 semanas * 7 días
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day);
      days.push({
        date,
        isCurrentMonth: false,
        isToday: this.isToday(date),
        events: this.getEventsForDate(date)
      });
    }
    
    this.calendarDays.set(days);
  }

  getEventsForDate(date: Date): LiveEvent[] {
    return this.liveEvents.filter(event => 
      event.date.getDate() === date.getDate() &&
      event.date.getMonth() === date.getMonth() &&
      event.date.getFullYear() === date.getFullYear()
    );
  }

  isToday(date: Date): boolean {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  }

  isSameDay(date1: Date | null, date2: Date): boolean {
    if (!date1) return false;
    return date1.getDate() === date2.getDate() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getFullYear() === date2.getFullYear();
  }

  selectDate(day: CalendarDay) {
    this.selectedDate.set(day.date);
  }

  previousMonth() {
    const current = this.currentDate();
    this.currentDate.set(new Date(current.getFullYear(), current.getMonth() - 1));
    this.loadEventos();
  }

  nextMonth() {
    const current = this.currentDate();
    this.currentDate.set(new Date(current.getFullYear(), current.getMonth() + 1));
    this.loadEventos();
  }

  goToToday() {
    this.currentDate.set(new Date());
    this.selectedDate.set(null);
    this.generateCalendar();
  }

  getCurrentMonthYear(): string {
    return `${this.months[this.currentDate().getMonth()]} ${this.currentDate().getFullYear()}`;
  }

  getUpcomingEvents(): LiveEvent[] {
    const now = new Date();
    return this.liveEvents
      .filter(event => event.date >= now)
      .sort((a, b) => a.date.getTime() - b.date.getTime())
      .slice(0, 5);
  }

  getSelectedDateEvents(): LiveEvent[] {
    if (!this.selectedDate()) return [];
    return this.getEventsForDate(this.selectedDate()!);
  }

  goToLiveClass(eventId: number) {
    this.router.navigate(['/live-class', eventId]);
  }

  formatDate(date: Date): string {
    return date.toLocaleDateString('es-ES', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  }

  getEventTime(event: LiveEvent): string {
    return event.date.toLocaleTimeString('es-ES', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  }

  getAttendancePercentage(event: LiveEvent): number {
    return Math.round((event.attendees / event.maxAttendees) * 100);
  }

  isEventFull(event: LiveEvent): boolean {
    return event.attendees >= event.maxAttendees;
  }
}

